import { FC, PropsWithChildren, useEffect } from 'react';

import { usePendingTemplates } from '@/store/pendingTemplates';
import { useQueryParam } from '@/hook/useQueryParam';
import { PENDING_TEMPLATES_QUERY_ARG } from '@/types/query.type';
import { logDebug } from '@/util/log.util';
import { PENDING_TEMPLATES_STORAGE_KEY } from '@/types/storage.type';
import { attempt } from '@/util/attempt.util';

type PendingTemplateProviderProps = PropsWithChildren<{}>;

export const PendingTemplateProvider: FC<PendingTemplateProviderProps> = ({
    children,
}) => {
    const { pendingTemplates, onChangePendingTemplates } =
        usePendingTemplates();

    const [templateIds] = useQueryParam({ name: PENDING_TEMPLATES_QUERY_ARG });

    useEffect(() => {
        const cachedPendingTemplates = attempt(() =>
            JSON.parse(
                localStorage.getItem(PENDING_TEMPLATES_STORAGE_KEY) || '[]',
            ),
        );

        if (!templateIds && !cachedPendingTemplates?.length) {
            return;
        }

        const templateIdsArray =
            (templateIds ? templateIds.split(',') : cachedPendingTemplates) ||
            [];

        logDebug('[PendingTemplateProvider] Setting pending templates', {
            templateIds: templateIdsArray,
        });

        // NOTE: Override any existing pending templates
        onChangePendingTemplates(templateIdsArray);
    }, []);

    useEffect(() => {
        if (!pendingTemplates.length) {
            attempt(() =>
                localStorage.removeItem(PENDING_TEMPLATES_STORAGE_KEY),
            );

            return;
        }

        attempt(() =>
            localStorage.setItem(
                PENDING_TEMPLATES_STORAGE_KEY,
                JSON.stringify(pendingTemplates),
            ),
        );
    }, [pendingTemplates]);

    return <>{children}</>;
};
